import styled, { css, type DefaultTheme } from 'styled-components';
import {
  NODE_CARD_DIMMED_OPACITY,
  NODE_CARD_EMPHASIS_DIMMED,
  NODE_CARD_EMPHASIS_HIGHLIGHTED,
  type NodeCardEmphasis,
} from '@aibuild/ui';

export type RunEdgeState = 'idle' | 'running' | 'done' | 'failed';

interface PathProps {
  $accent: 'default' | 'approved' | 'rejected';
  $selected: boolean;
  $published: boolean;
  $runState?: RunEdgeState;
}

// Wire mixes use `primaryBold`, not `primary` — our M3 `primary` is
// the design's `--secondary` and blends off-spec with the canvas.
const primaryBoldFor = (theme: DefaultTheme): string =>
  theme.colors?.primaryBold ?? '#8E8DFF';

const outlineVariantFor = (theme: DefaultTheme): string =>
  theme.colors?.outlineVariant ?? '#25263A';

const successFixedFor = (theme: DefaultTheme): string =>
  theme.colors?.successFixed ?? '#4ADE80';

const runErrorFor = (theme: DefaultTheme): string =>
  theme.colors?.runError ?? '#FF6B6B';

const isLightTheme = (theme: DefaultTheme): boolean => !!theme.isLightTheme;

const accentStroke = (
  $accent: PathProps['$accent'],
  theme: DefaultTheme,
): string => {
  if ($accent === 'approved') return theme.colors?.success ?? '#8CD88C';
  if ($accent === 'rejected') return theme.colors?.error ?? '#FFB4AB';
  // Match the node-card resting border so wires read as part of the
  // same hairline system (light uses a flat `outlineVariant`, dark
  // uses a primary-tinted blend close to the dashed outline).
  if (isLightTheme(theme)) {
    return outlineVariantFor(theme);
  }
  return `color-mix(in oklch, ${primaryBoldFor(theme)} 42%, ${outlineVariantFor(theme)})`;
};

const runStroke = (
  runState: RunEdgeState,
  theme: DefaultTheme,
): string | null => {
  if (runState === 'done') return successFixedFor(theme);
  if (runState === 'failed') return runErrorFor(theme);
  if (runState === 'running') return primaryBoldFor(theme);
  return null;
};

// Hover stays in the bright state colour so it reads as "passing
// through"; selected darkens the same hue so it reads as "locked in"
// — without darkening, hover and selected sat too close together
// visually (just thicker stroke + a bit more glow).
const darkenRunColor = (color: string): string =>
  `color-mix(in oklch, ${color} 85%, black)`;

const selectedRunEdgeCss = (color: string) => css`
  stroke: ${darkenRunColor(color)};
  stroke-width: 2.9;
  opacity: 1;
  stroke-dasharray: none;
  filter: drop-shadow(
    0 0 9px color-mix(in oklch, ${color} 60%, transparent)
  );
`;

const selectedRunEndpointCss = (color: string) => css`
  fill: ${darkenRunColor(color)};
  opacity: 1;
  r: 4;
  filter: drop-shadow(
    0 0 5px color-mix(in oklch, ${color} 65%, transparent)
  );
`;

const hoverRunEdgeCss = (color: string) => css`
  stroke: ${color};
  stroke-width: 2.5;
  stroke-dasharray: none;
  opacity: 1;
  filter: drop-shadow(
    0 0 7px color-mix(in oklch, ${color} 50%, transparent)
  );
`;

const hoverRunEndpointCss = (color: string) => css`
  fill: ${color};
  opacity: 1;
  r: 3.8;
`;

// Invisible 20px-wide hit target so the dashed draft stroke is clickable.
export const InteractionPath = styled.path`
  fill: none;
  stroke: transparent;
  stroke-width: 20;
  pointer-events: stroke;
`;

// `$published` deliberately doesn't change the dasharray — published is
// an authoring-mode lock, not a visual rewire.
export const EdgePath = styled.path<PathProps>`
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  pointer-events: stroke;
  transition:
    stroke 140ms ease,
    stroke-dasharray 140ms ease,
    stroke-width 140ms ease,
    opacity 140ms ease,
    filter 140ms ease;

  ${({ $accent, $selected, $runState, theme }) => {
    const run = $runState ? runStroke($runState, theme) : null;
    const isRun = run !== null;
    const stroke = run ?? accentStroke($accent, theme);
    const primary = primaryBoldFor(theme);
    if (isRun) {
      if ($selected && ($runState === 'done' || $runState === 'failed')) {
        return selectedRunEdgeCss(stroke);
      }
      if ($runState === 'running') {
        return css`
          stroke: ${stroke};
          stroke-width: 2.4;
          opacity: 1;
          stroke-dasharray: none;
          filter: drop-shadow(
            0 0 6px color-mix(in oklch, ${primary} 55%, transparent)
          );
        `;
      }
      return css`
        stroke: ${stroke};
        stroke-width: 2.2;
        opacity: 1;
        stroke-dasharray: none;
      `;
    }
    if ($selected) {
      return css`
        stroke: ${primary};
        stroke-width: 2.75;
        opacity: 1;
        stroke-dasharray: none;
        filter: drop-shadow(
          0 0 8px color-mix(in oklch, ${primary} 60%, transparent)
        );
      `;
    }
    return css`
      stroke: ${stroke};
      stroke-width: 1.75;
      opacity: ${isLightTheme(theme) ? 1 : 0.82};
      stroke-dasharray: 1 5;
    `;
  }}
`;

const endpointRestingFill = (theme: DefaultTheme): string => {
  const primary = primaryBoldFor(theme);
  if (isLightTheme(theme)) return primary;
  return `color-mix(in oklch, ${primary} 75%, white)`;
};

export const WireEndpoint = styled.circle<PathProps>`
  pointer-events: none;
  transition:
    fill 140ms ease,
    r 140ms ease,
    filter 140ms ease;
  ${({ $selected, $runState, theme }) => {
    const primary = primaryBoldFor(theme);
    const runColor = $runState ? runStroke($runState, theme) : null;
    if (
      $selected &&
      runColor !== null &&
      ($runState === 'done' || $runState === 'failed')
    ) {
      return selectedRunEndpointCss(runColor);
    }
    if ($runState === 'failed') {
      return css`
        fill: ${runErrorFor(theme)};
        opacity: 1;
        r: 3.5;
      `;
    }
    if ($runState === 'done') {
      return css`
        fill: ${successFixedFor(theme)};
        opacity: 1;
        r: 3.5;
      `;
    }
    if ($runState === 'running') {
      return css`
        fill: ${primary};
        opacity: 1;
        r: 3.5;
        filter: drop-shadow(
          0 0 4px color-mix(in oklch, ${primary} 55%, transparent)
        );
      `;
    }
    if ($selected) {
      return css`
        fill: ${primary};
        opacity: 1;
        r: 4;
        filter: drop-shadow(
          0 0 5px color-mix(in oklch, ${primary} 70%, transparent)
        );
      `;
    }
    return css`
      fill: ${endpointRestingFill(theme)};
      opacity: 0.9;
      r: 3;
    `;
  }}
`;

const litWireCss = (runState: RunEdgeState, theme: DefaultTheme) => {
  if (runState === 'done' || runState === 'failed') {
    const color =
      runState === 'done' ? successFixedFor(theme) : runErrorFor(theme);
    return css`
      ${EdgePath} {
        ${hoverRunEdgeCss(color)}
      }
      ${WireEndpoint} {
        ${hoverRunEndpointCss(color)}
      }
    `;
  }
  const primary = primaryBoldFor(theme);
  /* Light skips the white-mix — pushes to a near-white pastel
     that loses contrast on lavender paper. */
  const hoverStroke = isLightTheme(theme)
    ? primary
    : `color-mix(in oklch, ${primary} 75%, white)`;
  return css`
    ${EdgePath} {
      stroke: ${hoverStroke};
      stroke-width: 2.25;
      stroke-dasharray: none;
      opacity: 1;
      filter: drop-shadow(
        0 0 5px color-mix(in oklch, ${primary} 38%, transparent)
      );
    }
    ${WireEndpoint} {
      fill: ${primary};
      opacity: 1;
      r: 3.5;
    }
  `;
};

// Hover and the connection-focus highlight share one "lit" look. Both are
// suppressed when selected (selected style already wins) and for
// `running` (the live drop-shadow pulse would clash with a halo). `done`
// and `failed` get a same-colour halo; `idle` uses the lavender palette.
export const WireGroup = styled.g<{
  $selected: boolean;
  $runState: RunEdgeState;
  $focus: NodeCardEmphasis | null;
}>`
  transition: opacity 140ms ease;

  ${({ $focus }) =>
    $focus === NODE_CARD_EMPHASIS_DIMMED &&
    css`
      opacity: ${NODE_CARD_DIMMED_OPACITY};

      &:hover {
        opacity: 1;
      }
    `}

  ${({ $selected, $runState, $focus, theme }) => {
    if ($selected) return '';
    if ($runState === 'running') return '';
    const lit = litWireCss($runState, theme);
    return css`
      &:hover {
        ${lit}
      }
      ${$focus === NODE_CARD_EMPHASIS_HIGHLIGHTED ? lit : ''}
    `;
  }}
`;
